import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { DebounceSettings } from 'lodash'; // eslint-disable-line lodash/import-scope -- Doesn't apply to importing types
import type { ActionCreator, AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

import { useDebouncedCallback } from '../../../../hooks';
import * as ProjectsV2Store from '../../../../redux/projectsV2';

const DEBOUNCE_TIMEOUT = 750;

//  We want requests to happen after the last user action takes place.
//  Read more: https://css-tricks.com/debouncing-throttling-explained-examples/#aa-debounce
const DEBOUNCE_OPTIONS: DebounceSettings = {
  leading: false,
  trailing: true,
};

/**
 * Hook which will return a function which dispatches a given action
 * first, followed by dispatching a debounced version of the async fetchProjectsAction.
 *
 * @example
 * const MyComponent = ({ page }) => {
 *   // Function which will dispatch the updatePagination action, then the fetchProjects action
 *   const updatePagination = useFetchProjectsAction(ProjectsV2Store.actions.updatePagination);
 *   // ...
 *   return (
 *     <button onClick={() => updatePagination({ page: page + 1 })}>Next Page</button>
 *   );
 * }
 */
const useFetchProjectsAction = (
  actionCreator: ActionCreator<AnyAction> | ActionCreator<ThunkAction<void, any, never, AnyAction>>,
) => {
  const dispatch = useDispatch();

  //  Memoized debounce function, which re-fetches the projects.
  const dispatchFetchProjects = useDebouncedCallback(() => {
    dispatch(ProjectsV2Store.actions.fetchProjects());
  }, DEBOUNCE_TIMEOUT, DEBOUNCE_OPTIONS);

  const fetchProjects = useCallback((...args: unknown[]) => {
    /**
     * First we dispatch the initial action,
     * which will update the Redux state immediately.
     */
    dispatch(actionCreator(...args));

    /**
     * Because the next async fetch action is debounced, the UI will not appear
     * that it is loading until the debounce function is invoked,
     * which will be 750ms after the last user action.
     *
     * To avoid this, we first dispatch an action so that the UI will
     * show the loading UI immediately.
     */
    dispatch(ProjectsV2Store.actions.preFetchLoading());

    /**
     * ...then we dispatch the fetchProjectsAction
     *
     * Note that Redux actions are synchronous by default, meaning that the reducer will
     * have been called from the previous action's dispatch.
     * This is how the fetchProjectsAction (an async thunk) is able to read the
     * latest state and use that to make the request.
     */
    dispatchFetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionCreator]);

  return fetchProjects;
};

export default useFetchProjectsAction;
